import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Loader2 } from "lucide-react";
import Seo from "@/components/Seo";
import Reveal from "@/components/Reveal";
import { useConsultation } from "@/components/ConsultationProvider";
import { imgUrl } from "@/utils/media";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Content({ text }) {
  const blocks = String(text || "").split(/\n\s*\n/);
  return (
    <div className="space-y-5">
      {blocks.map((block, i) => {
        const trimmed = block.trim();
        if (!trimmed) return null;
        if (trimmed.startsWith("## ")) {
          return (
            <h2 key={i} className="font-display text-2xl text-amber-900 pt-4">{trimmed.slice(3)}</h2>
          );
        }
        if (trimmed.split("\n").every((l) => l.trim().startsWith("- "))) {
          return (
            <ul key={i} className="list-disc pl-6 space-y-2 text-stone-700">
              {trimmed.split("\n").map((l, j) => (
                <li key={j}>{l.trim().slice(2)}</li>
              ))}
            </ul>
          );
        }
        return <p key={i} className="text-stone-700 leading-relaxed whitespace-pre-line">{trimmed}</p>;
      })}
    </div>
  );
}

export default function GuidePost() {
  const { slug } = useParams();
  const { open } = useConsultation();
  const [post, setPost] = useState(undefined);

  useEffect(() => {
    fetch(`${API}/posts/${slug}`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setPost)
      .catch(() => setPost(null));
  }, [slug]);

  if (post === undefined) {
    return <div className="min-h-screen flex items-center justify-center bg-[#FAF8F5]"><Loader2 className="w-6 h-6 text-amber-700 animate-spin" /></div>;
  }
  if (!post) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-[#FAF8F5] px-4 text-center pt-16">
        <h1 className="font-display text-2xl text-stone-900 mb-3">Guide not found</h1>
        <Link to="/guides" className="text-amber-700 font-semibold text-sm">← All flooring guides</Link>
      </div>
    );
  }

  return (
    <div className="pt-16">
      <Seo title={`${post.title} | Crownvera Epoxy`} description={post.excerpt || post.title} />
      <article className="max-w-3xl mx-auto px-4 sm:px-8 pt-14 pb-24" data-testid="guide-post">
        <Reveal>
          <Link to="/guides" data-testid="guide-back" className="inline-flex items-center gap-2 text-xs uppercase tracking-wider text-stone-500 hover:text-amber-700 transition-colors duration-300 mb-8">
            <ArrowLeft className="w-4 h-4" /> All flooring guides
          </Link>
          <p className="text-xs text-stone-400">{new Date(post.created_at).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</p>
          <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-stone-900 leading-tight mt-3">{post.title}</h1>
          {post.image && (
            <div className="mt-8 rounded-xl overflow-hidden border border-amber-600/25 shadow-[0_30px_60px_-30px_rgba(184,134,11,0.35)]">
              <img src={imgUrl(post.image)} alt={post.title} className="w-full max-h-[420px] object-cover" />
            </div>
          )}
          <div className="mt-10">
            <Content text={post.content} />
          </div>
          <div className="mt-14 gold-ring-card rounded-xl p-7 sm:p-9 text-center">
            <h2 className="font-display text-2xl text-stone-900">Ready to see it on your floor?</h2>
            <p className="text-sm text-stone-600 mt-2">Free in-home consultations across Cleveland &amp; Northeast Ohio — every finish backed by our 10-year warranty.</p>
            <button
              onClick={open}
              data-testid="guide-cta-btn"
              className="mt-6 bg-amber-600 hover:bg-amber-700 text-white font-bold uppercase tracking-wider text-sm px-8 py-4 rounded-md shadow-md transition-colors duration-300"
            >
              Schedule Your Free Consultation
            </button>
          </div>
        </Reveal>
      </article>
    </div>
  );
}
